import { useState, useEffect, useCallback } from "react";

const CACHE_KEY = "openrouter_models_v5";
const OLD_CACHE_KEY = "openrouter_models_cache";
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

export interface OpenRouterModel {
  id: string;
  name: string;
  description: string;
  context_length: number;
  pricing: {
    prompt: string;
    completion: string;
  };
  architecture: {
    input_modalities: string[];
    output_modalities: string[];
  };
  created?: number;
}

interface CacheEntry {
  timestamp: number;
  data: OpenRouterModel[];
}

function getCache(): OpenRouterModel[] | null {
  try {
    // Clean up old cache keys
    localStorage.removeItem(OLD_CACHE_KEY);
    localStorage.removeItem("openrouter_models_v2");
    localStorage.removeItem("openrouter_models_v3");

    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    if (!Array.isArray(entry.data) || entry.data.length === 0) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return entry.data;
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

function setCache(data: OpenRouterModel[]) {
  try {
    // Strip long descriptions to fit within localStorage quota (~5MB)
    const slim = data.map((m) => ({
      ...m,
      description: (m.description || "").substring(0, 200),
    }));
    const entry: CacheEntry = { timestamp: Date.now(), data: slim };
    const json = JSON.stringify(entry);
    // Safety check — don't try to store > 4MB
    if (json.length > 4_000_000) {
      console.warn("OpenRouter model cache too large, skipping cache");
      return;
    }
    localStorage.setItem(CACHE_KEY, json);
  } catch {
    // localStorage full — silently ignore
  }
}

function stripModel(raw: any): OpenRouterModel {
  return {
    id: raw.id,
    name: raw.name || raw.id,
    description: raw.description || "",
    context_length: raw.context_length || 0,
    pricing: {
      prompt: raw.pricing?.prompt || "0",
      completion: raw.pricing?.completion || "0",
    },
    architecture: {
      input_modalities: raw.architecture?.input_modalities || ["text"],
      output_modalities: raw.architecture?.output_modalities || [],
    },
    created: raw.created,
  };
}

/**
 * Extract a "family key" from a model ID so we can group related versions.
 * e.g. "anthropic/claude-opus-4.6" → "anthropic/claude-opus"
 *      "openai/gpt-4.1-mini"      → "openai/gpt-4-mini"
 *      "google/gemini-2.5-flash"   → "google/gemini-flash"
 * We strip version numbers (digits, dots) and date suffixes so that
 * "claude-opus-4" and "claude-opus-4.6" land in the same family.
 */
function familyKey(id: string): string {
  // Remove :free / :extended etc. suffixes
  let clean = id.replace(/:[a-z]+$/, "");
  // Remove trailing date stamps like -20240307 or -2512
  clean = clean.replace(/-\d{4,}$/, "");
  // Replace version-like segments: sequences of digits/dots immediately
  // before the end or before a hyphen.  e.g. "-4.6" → "", "-2.5-" → "-"
  // Keep a single canonical form so different versions collapse.
  clean = clean.replace(/[.-]\d+(\.\d+)*/g, "");
  return clean;
}

/**
 * Within each model family, remove older models that are the same price
 * or more expensive than a newer model (strictly dominated).
 */
function pruneSuperseded(models: OpenRouterModel[]): OpenRouterModel[] {
  // 1. Group by family + free/paid distinction
  const families = new Map<string, OpenRouterModel[]>();
  for (const m of models) {
    const isFree =
      parseFloat(m.pricing.prompt) === 0 &&
      parseFloat(m.pricing.completion) === 0;
    const key = familyKey(m.id) + (isFree ? ":free" : "");
    const arr = families.get(key) || [];
    arr.push(m);
    families.set(key, arr);
  }

  // 2. Within each family, keep only the newest when prices are equal or worse
  const kept = new Set<string>();
  families.forEach((siblings) => {
    if (siblings.length <= 1) {
      siblings.forEach((m: OpenRouterModel) => kept.add(m.id));
      return;
    }
    // Sort by creation date descending (newest first); missing date = oldest
    const sorted = [...siblings].sort(
      (a, b) => (b.created || 0) - (a.created || 0),
    );
    // The newest model is always kept
    kept.add(sorted[0].id);
    const newestPrompt = parseFloat(sorted[0].pricing.prompt);
    const newestCompletion = parseFloat(sorted[0].pricing.completion);

    for (let i = 1; i < sorted.length; i++) {
      const olderPrompt = parseFloat(sorted[i].pricing.prompt);
      const olderCompletion = parseFloat(sorted[i].pricing.completion);
      // Keep the older model only if it's strictly cheaper on at least one axis
      if (olderPrompt < newestPrompt || olderCompletion < newestCompletion) {
        kept.add(sorted[i].id);
      }
      // Otherwise it's same price or more expensive → dominated → skip
    }
  });

  return models.filter((m) => kept.has(m.id));
}

async function fetchModels(): Promise<OpenRouterModel[]> {
  const cached = getCache();
  if (cached) return cached;

  const res = await fetch("https://openrouter.ai/api/v1/models");
  if (!res.ok) throw new Error(`OpenRouter API error: ${res.status}`);
  const json = await res.json();
  const all: OpenRouterModel[] = (json.data || [])
    .filter((m: any) => m.id && m.pricing)
    .map(stripModel);
  const models = pruneSuperseded(all);
  setCache(models);
  return models;
}

export function useOpenRouterModels() {
  // Initialize with cached data synchronously to avoid flash of empty state
  const [models, setModels] = useState<OpenRouterModel[]>(
    () => getCache() || [],
  );
  const [loading, setLoading] = useState(() => (getCache() || []).length === 0);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      localStorage.removeItem(CACHE_KEY);
      const data = await fetchModels();
      setModels(data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch models");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we already have cached data from the synchronous init, still fetch
    // fresh data in the background to keep the cache updated
    let cancelled = false;
    fetchModels()
      .then((data) => {
        if (!cancelled) setModels(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to fetch models");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { models, loading, error, refresh };
}
